import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';

const Wrap = styled.div`
  height: 32px;
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  align-items: center;
  position: relative;
`;

const Left = styled.div`
  height: 30px;
  padding: 0 8px;
  border-right: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
`;

const StyledInput = styled(Input)`
  border: 0;
  border-radius: 4px;
  height: 20px;
  font-size: 11px;
  font-weight: 700;
  width: 60px;
`;

const ResetButton = styled.button`
  height: 20px;
  width: 42px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.lightGray};
`;

const Label = styled(Text)`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
`;

interface Props {
  activePage: EditorialQueuePage;
}

const ClicksThresholdInput: FC<Props> = ({ activePage }) => {
  const { network } = useParams();
  const queryClient = useQueryClient();
  const toasts = useToasts();

  const [value, setValue] = useState('');

  // eslint-disable-next-line
  const save = useCallback(
    debounce(async (value: string) => {
      const toastId = toasts.create('loading', 'Saving...');
      try {
        await api.post(`/v1/editorial/queue/settings`, {
          auto_delete_threshold: value || null,
          network:
            network === 'instagram'
              ? EditorialFacebookPostNetwork.INSTAGRAM
              : EditorialFacebookPostNetwork.FACEBOOK,
          page_id: activePage.id,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_queue_channels',
        ]);

        toasts.success(toastId, 'Saved');
      } catch (err) {
        toasts.error(toastId, 'Invalid site visits input!');
      }
    }, 3000),
    [],
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    save(e.target.value);
  };

  useEffect(() => {
    const n =
      network === 'instagram'
        ? EditorialFacebookPostNetwork.INSTAGRAM
        : EditorialFacebookPostNetwork.FACEBOOK;

    const settings = activePage?.settings.find((s) => s.network === n);

    setValue(settings?.autoDeleteThreshold?.toString() ?? '');
  }, [activePage, network]);

  return (
    <Wrap>
      <Label as="div" $type="smallHeadline">
        Site Visits %
      </Label>
      <Left>
        <ResetButton>
          <Text
            $type="extraSmall"
            $color="gray"
            as="span"
            onClick={() => setValue('')}
          >
            Reset
          </Text>
        </ResetButton>
      </Left>
      <StyledInput
        placeholder="Enter number"
        value={value}
        onChange={onChange}
      />
    </Wrap>
  );
};

export default ClicksThresholdInput;
